import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearSession, readSession, writeSession } from '@/shared/api/session';

/**
 * The one HTTP instance every generated hook goes through (§5.2, §5.5 in spirit:
 * one shared client, not one per feature).
 *
 * ⚠️ The base URL carries NO path.
 *
 * The generated operations already include the full `/api/v1/...` path from the OpenAPI
 * document, so a base of `/api` produced `/api/api/v1/theme/active` and a wall of 404s.
 * Caught by reading the network panel, not the code — the URL looks right in both halves.
 *
 * Empty in development, where Vite proxies `/api` to the backend. In production this is
 * the API's own origin (`https://api.<domain>`, §14), still with no path.
 */
export const http = axios.create({
  baseURL: import.meta.env['VITE_API_BASE_URL'] ?? '',
  headers: { 'Content-Type': 'application/json' },

  // No cookies. The API authenticates with a bearer token (§12.1) and the admin
  // panel is served from a different subdomain than the API (§14), so a
  // cookie-based session would need CORS credentials and a CSRF defence for no
  // gain.
  withCredentials: false,
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const session = readSession();

  if (session) {
    config.headers.set('Authorization', `Bearer ${session.accessToken}`);
  }

  return config;
});

/**
 * Refreshes the session at most once per 401, and shares one in-flight refresh
 * between concurrent failures.
 *
 * Without the sharing, a dashboard that fires six queries on load would send six
 * refresh requests — and because §12.1 ROTATES the token on use, five of them
 * would present an already-rotated token. The API treats a replayed rotated token
 * as theft and revokes every session (see RefreshSessionCommandHandler), so the
 * user would be logged out by their own dashboard.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const session = readSession();
  if (!session) {
    return null;
  }

  try {
    // Deliberately a bare axios call, not `http`: going through the instance would
    // re-enter this interceptor on failure and recurse.
    const response = await axios.post<{
      accessToken: string;
      accessTokenExpiresAt: string;
      refreshToken: string;
      refreshTokenExpiresAt: string;
    }>(`${http.defaults.baseURL}/api/v1/auth/refresh`, { refreshToken: session.refreshToken });

    writeSession({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
      accessTokenExpiresAt: response.data.accessTokenExpiresAt,
    });

    return response.data.accessToken;
  } catch (error) {
    // ⚠️ **Only a refusal ends the session. A refresh that never reached the server does not.**
    // Measured on 5 September 2026: the API was restarted for a minute, the proactive renewal
    // below fired into the gap, and the administrator was signed out of a panel they had not
    // touched. A network error says nothing about the token — the next renewal answers that —
    // and the mobile app makes the same distinction for a rescuer with no signal. Ending the
    // session here on a refused token is right: the server has said it no longer stands.
    //
    // ⚠️ **And only a 401 is a refusal.** Measured the same evening, second attempt: the dev proxy
    // answers an API that is down with a 500 of its own, and a reverse proxy in production answers
    // it with 502/503/504 — responses with a status, none of them the server saying anything about
    // the token. Only the API itself says «no», and it says it with 401.
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      clearSession();
    }

    return null;
  }
}

/**
 * Renews the access token before it expires, rather than after something fails.
 *
 * ⚠️ **A panel nobody is touching makes no requests, so nothing ever provokes the 401 that the
 * interceptor above refreshes on.** §12.1 keeps an access token to fifteen minutes and EMQX
 * disconnects a client the moment its token does — measured on the live broker, where an idle
 * control room reconnected every few seconds with a dead credential and the log filled with
 * `bad_username_or_password`. Nothing on screen changed, and nothing on screen said so: the live
 * feed had simply stopped.
 *
 * ⚠️ It shares `refreshInFlight` with the interceptor, and that is not tidiness. §12.1 ROTATES the
 * refresh token on use, and the API treats a replayed rotated token as theft and revokes every
 * session — so a proactive renewal racing a 401-driven one would sign the operator out of their own
 * control room.
 */
export function renewSession(): Promise<string | null> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

type RetriedConfig = InternalAxiosRequestConfig & { _retried?: boolean };

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriedConfig | undefined;

    if (error.response?.status !== 401 || !config || config._retried) {
      return Promise.reject(error);
    }

    // The refresh call itself failing must not trigger another refresh.
    if (config.url?.includes('/auth/')) {
      clearSession();
      return Promise.reject(error);
    }

    config._retried = true;

    refreshInFlight ??= refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });

    const token = await refreshInFlight;
    if (!token) {
      return Promise.reject(error);
    }

    config.headers.set('Authorization', `Bearer ${token}`);
    return http.request(config);
  },
);

/**
 * The mutator orval calls. Returns the body, so generated hooks are typed as the
 * DTO rather than as an axios envelope.
 */
export const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response: AxiosResponse<T> = await http.request<T>(config);
  return response.data;
};

export default request;
